/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";

import { TokenManager } from "../../../model/auth";
import { ProfileManagementInteractor } from "../../../model/interactor";
import { Locale, LocaleKeys } from "../../../model/locale";
import { Log } from "../../../model/logging";
import {
  StorageAdapter,
  STORAGE_KEY_ACTIVE_PROFILE_SELECTION
} from "../../../model/storage";
import type {
  TActivateProfileParams,
  TActivateProfileResult,
  TActiveProfileSelection,
  TProfileError
} from "../profiles.types";
import {
  isProfileError,
  isProfileNotFoundError,
  normalizeProfileError
} from "../profileErrorHandling";
import { deactivateProfileAuthentication } from "./profileDeactivation.thunks";
import { loadProfile } from "./profileLoading.thunks";

const TAG: string = "profileActivationThunks";

/**
 * Returns the app to the account context.
 *
 * The account token is the default: it serves every request until a profile is
 * selected, and it takes over again when the active profile goes away. Nothing
 * needs to be fetched here — TokenManager refreshes the account token by
 * itself on the next request if it has gone stale.
 */
export async function restoreAccountTokenContext(): Promise<void> {
  await TokenManager.setActiveContext(null);
}

/** Writes the selection the profile gate reads back on the next launch. */
async function persistSelection(
  selection: TActiveProfileSelection
): Promise<void> {
  await StorageAdapter.getInstance().set(
    STORAGE_KEY_ACTIVE_PROFILE_SELECTION,
    selection,
    Number.POSITIVE_INFINITY
  );
}

function buildProfileSelection(
  params: TActivateProfileParams
): TActiveProfileSelection {
  return {
    activeProfileId: params.profileId,
    isSkipSelectedProfileEnabled: params.isSkipSelectedProfileEnabled
  };
}

async function activateProfileContext(
  profileId: string,
  signal: AbortSignal
): Promise<void> {
  const profileInteractor: ProfileManagementInteractor =
    new ProfileManagementInteractor();
  await profileInteractor.setSelectedProfile(
    { profileId: profileId },
    { signal: signal }
  );

  // Resolve the profile token before changing the active context. A failed
  // activation therefore leaves the account context usable.
  await TokenManager.getValidAccessToken(profileId);
  await TokenManager.setActiveContext(profileId);
}

export const activateProfile = createAsyncThunk<
  TActivateProfileResult,
  TActivateProfileParams,
  { rejectValue: TProfileError | string }
>(
  "profiles/activateProfile",
  async (params, { dispatch, rejectWithValue, signal }) => {
    try {
      Log.d(TAG, "activateProfile() called");
      const selection: TActiveProfileSelection = buildProfileSelection(params);

      // Re-picking the profile that is already serving requests changes nothing
      // the services can see: PMS already has it as the selected profile, and
      // TokenManager already holds its token. Re-running the activation would
      // re-notify PMS and, through the `activateProfile.fulfilled` listener,
      // re-request every profile-scoped service for the profile the user is
      // already on. The preference is still written, because the skip-next-time
      // checkbox may be what the user actually changed.
      const isAlreadyActive: boolean =
        TokenManager.getActiveProfileId() === params.profileId;
      if (isAlreadyActive) {
        await dispatch(loadProfile({ profileId: params.profileId })).unwrap();
        Log.d(
          TAG,
          "activateProfile() already the active profile — no re-fetch"
        );
        await persistSelection(selection);
        return { selection: selection, didActivate: false };
      }

      await activateProfileContext(params.profileId, signal);

      await persistSelection(selection);
      return { selection: selection, didActivate: true };
    } catch (error: unknown) {
      Log.e(TAG, "activateProfile()", error);
      if (isProfileError(error) && isProfileNotFoundError(error)) {
        await dispatch(deactivateProfileAuthentication(params.profileId));
      }
      return rejectWithValue(
        normalizeProfileError(
          error,
          Locale.getString(LocaleKeys.PROFILE_ACTIVATE_FAILED_FALLBACK)
        )
      );
    }
  }
);
